"use client";

import { OFFSTREET_COMPANY_LOGO } from "@offstreetparking/design-system/providers";
import { Heading, Text } from "@offstreetparking/design-system/typography";
import Image from "next/image";
import { type PropsWithChildren, useMemo } from "react";
import { useCompany } from "~/components/providers/CompanyProvider";

type AuthCardLayoutProps = {
	title: React.ReactNode;
	description?: React.ReactNode;
};

export const AuthCardLayout = ({ title, description, children }: PropsWithChildren<AuthCardLayoutProps>) => {
	const { company } = useCompany();

	const logo = useMemo(() => {
		if (!company?.branding?.companyLogoFileUrl) {
			return {
				url: OFFSTREET_COMPANY_LOGO,
				altText: "Offstreet",
			};
		}
		return {
			url: company.branding.companyLogoFileUrl,
			altText: company.name,
		};
	}, [company]);

	return (
		<div className="w-full space-y-6 px-4 py-8 sm:px-8">
			<div className="flex flex-col items-center gap-3 text-center">
				<Image
					priority
					src={logo.url}
					width={0}
					height={0}
					sizes="160px"
					alt={logo.altText}
					style={{
						maxHeight: "64px",
						width: "auto",
						maxWidth: "160px",
					}}
				/>
				<Heading As="h1" kind="h1">
					{title}
				</Heading>
				{description && (
					<Text intent="muted" size="sm">
						{description}
					</Text>
				)}
			</div>
			{children}
		</div>
	);
};
